import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'

/**
 * Posts an account must have published before Takeaways are worth generating.
 * One or two posts give the analysis almost nothing to work with, so an account
 * that has only tried Buffer out is better served by the get-started panel than
 * by a thin takeaway.
 */
export const MIN_POSTS_FOR_TAKEAWAYS = 3

export interface TakeawaysGate {
  /** The count has not arrived, so the answer below is not yet known. */
  loading: boolean
  /**
   * True only once the scope is known to have published enough. False while
   * `loading`, so a caller can skip on this flag alone and never ask early.
   */
  shouldRequestTakeaways: boolean
}

export function hasEnoughPostsForTakeaways(sentCount: number): boolean {
  return sentCount >= MIN_POSTS_FOR_TAKEAWAYS
}

interface UseTakeawaysGateOptions {
  /** Withhold to hold the gate at `loading` — the count query skips without it. */
  organizationId?: string
  /** Channels to count. Omit to ask the question of the whole organization. */
  channelIds?: ReadonlyArray<string>
}

/**
 * Decides whether a scope has posted enough for Takeaways to be worth showing —
 * and worth generating, which costs an LLM call whether or not there is anything
 * to say. Callers skip their Takeaways query until `shouldRequestTakeaways`
 * turns true, because Apollo cannot recall a request once sent.
 *
 * The count is every post the scope has ever sent, never the ones inside the
 * selected period: the question is whether the scope posts at all, so no filter
 * the page applies may narrow it.
 */
export function useTakeawaysGate({
  organizationId,
  channelIds,
}: UseTakeawaysGateOptions): TakeawaysGate {
  const { counts, loading } = usePostCounts({
    organizationId,
    channelIds: channelIds ? [...channelIds] : undefined,
  })

  return {
    loading,
    shouldRequestTakeaways:
      !loading && hasEnoughPostsForTakeaways(counts?.sent ?? 0),
  }
}
